import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  CheckCircle2,
  Clock,
  MapPin,
  Users,
  ChevronRight,
  Share2,
  Heart,
  ShieldCheck,
  Receipt,
  Sparkles,
  AlertTriangle,
  FileText,
  IndianRupee,
  TrendingUp,
  Calendar,
} from "lucide-react";
import { SiteShell } from "@/components/site-shell";
import { Badge, CauseCard } from "@/components/cards";
import {
  causes,
  donationAmounts,
  formatINR,
  getCause,
  getInstitution,
  progressPct,
  IMG,
} from "@/data/sample";

export const Route = createFileRoute("/causes/$id")({
  loader: ({ params }) => {
    const c = getCause(params.id);
    if (!c) throw notFound();
    return c;
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: `${loaderData?.title ?? "Cause"} — Animal Seva` },
      { name: "description", content: loaderData?.shortDesc ?? "" },
      { property: "og:image", content: loaderData?.image ?? "" },
    ],
  }),
  component: CauseDetail,
  notFoundComponent: () => (
    <SiteShell>
      <div className="mx-auto max-w-2xl py-24 px-6 text-center">
        <h1 className="font-display text-2xl">Cause not found</h1>
        <Link to="/causes" className="mt-4 inline-block text-[var(--maroon)] underline">
          Back to causes
        </Link>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ reset }) => (
    <SiteShell>
      <div className="mx-auto max-w-2xl py-24 px-6 text-center">
        <p>Something went wrong.</p>
        <button onClick={reset} className="mt-4 text-[var(--maroon)] underline">
          Try again
        </button>
      </div>
    </SiteShell>
  ),
});

function CauseDetail() {
  const c = Route.useLoaderData();
  const inst = getInstitution(c.institutionId);
  const pct = progressPct(c);
  const remaining = Math.max(0, c.required - c.raised);
  const similar = causes.filter((x) => x.id !== c.id && x.category === c.category).slice(0, 3);
  const daysLeft = Math.max(0, Math.ceil((new Date(c.endDate).getTime() - Date.now()) / 86400000));
  const tiers = [
    { amount: 501, impact: "Feeds 3 animals for a day" },
    { amount: 1001, impact: "Covers basic medicine for one animal" },
    { amount: 2501, impact: "Sponsors weekly fodder for 5 cattle" },
    { amount: 5001, impact: "Funds a minor surgery or check-up" },
    { amount: 11001, impact: "Sponsors monthly care for 10 animals" },
    { amount: 25001, impact: "Critical care package — full recovery" },
  ];
  const breakdown = [
    { label: "Direct cause expenses", pct: 88, color: "var(--maroon)" },
    { label: "Logistics & delivery", pct: 7, color: "var(--saffron-deep)" },
    { label: "Platform & payment fees", pct: 5, color: "#9b8a6e" },
  ];
  const topDonors = [
    { name: "Rahul S.", city: "Mumbai", amount: 11001 },
    { name: "Anonymous", city: "—", amount: 5001 },
    { name: "Priya M.", city: "Bengaluru", amount: 5001 },
    { name: "Amit J.", city: "Surat", amount: 2501 },
    { name: "Neha A.", city: "Delhi", amount: 1001 },
  ];
  const updates = [
    {
      date: "18 Jun 2026",
      title: "Cause approved & published",
      body: "Verified by admin team. Documents on file.",
    },
    {
      date: "20 Jun 2026",
      title: "First milestone reached",
      body: "25% funded — first batch of supplies dispatched.",
    },
    {
      date: "23 Jun 2026",
      title: "On-ground photos shared",
      body: "Updates emailed to all donors with expense bills.",
    },
  ];
  const faqs = [
    {
      q: "Where does my donation go?",
      a: `100% reaches ${inst?.name}. Funds are released against approved milestones and matched with expense proof.`,
    },
    {
      q: "Will I get a tax receipt?",
      a: inst?.has80G
        ? "Yes — 80G receipt is emailed within minutes."
        : "A regular donation receipt is sent. This cause does not qualify for 80G deduction.",
    },
    {
      q: "What if the cause is over-funded?",
      a: "Any surplus is routed to the next priority cause from the same institution, with donor consent.",
    },
    {
      q: "How do I track impact?",
      a: "You will receive photo + bill updates within 30 days of donating, plus a final completion report.",
    },
  ];
  const gallery = [c.image, IMG.heroCow, IMG.heroDog, IMG.heroBird];

  return (
    <SiteShell>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-[var(--cream-2)]">
        <div className="mx-auto flex max-w-7xl items-center gap-1.5 px-4 py-3 text-xs text-foreground/60 sm:px-6">
          <Link to="/" className="hover:text-[var(--maroon)]">
            Home
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/causes" className="hover:text-[var(--maroon)]">
            Causes
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground/80 truncate">{c.title}</span>
        </div>
      </div>

      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-10 sm:px-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-10">
          {/* Hero image with overlay */}
          <div className="relative overflow-hidden rounded-3xl shadow-[var(--shadow-card)]">
            <img src={c.image} alt={c.title} className="aspect-[16/9] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--maroon-deep)]/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-[var(--cream)] sm:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="featured">{c.category}</Badge>
                {c.urgency === "Critical" && (
                  <Badge tone="critical">
                    <AlertTriangle className="h-3 w-3" />
                    Critical
                  </Badge>
                )}
                {c.urgency === "Urgent" && <Badge tone="urgent">Urgent</Badge>}
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] backdrop-blur">
                  {c.animalType}
                </span>
              </div>
              <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">{c.title}</h1>
              <p className="mt-2 flex flex-wrap items-center gap-3 text-sm text-[var(--cream)]/85">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {inst?.city}, {inst?.state}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {daysLeft} days left
                </span>
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {c.donors} donors
                </span>
              </p>
            </div>
          </div>

          {/* Institution strip */}
          <Link
            to="/institutions/$id"
            params={{ id: c.institutionId }}
            className="flex items-center justify-between gap-4 rounded-2xl border border-border/60 bg-card p-4 shadow-[var(--shadow-soft)] hover:border-[var(--maroon)]/30"
          >
            <div className="flex items-center gap-4 min-w-0">
              <img
                src={inst?.cover}
                alt=""
                className="h-14 w-14 shrink-0 rounded-xl object-cover"
              />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-foreground/70">Run by</p>
                <p className="font-display text-base font-semibold text-foreground truncate">
                  {inst?.name}
                </p>
                <div className="mt-0.5 flex flex-wrap items-center gap-2">
                  {inst?.verified && (
                    <Badge tone="verified">
                      <CheckCircle2 className="h-3 w-3" />
                      Verified
                    </Badge>
                  )}
                  {inst?.has80G && <Badge tone="featured">80G</Badge>}
                  <span className="text-[11px] text-foreground/60">Est. {inst?.established}</span>
                </div>
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[var(--maroon)]">
              Visit institution <ChevronRight className="h-4 w-4" />
            </span>
          </Link>

          {/* Stats */}
          <div className="grid gap-4 sm:grid-cols-4">
            <Mini label="Required" value={formatINR(c.required)} icon={IndianRupee} />
            <Mini label="Raised" value={formatINR(c.raised)} accent icon={TrendingUp} />
            <Mini label="Remaining" value={formatINR(remaining)} icon={Receipt} />
            <Mini label="Days left" value={String(daysLeft)} icon={Calendar} />
          </div>

          {/* About */}
          <Block title="About this cause" eyebrow="Why we need you">
            <p className="text-base leading-relaxed text-foreground/80">{c.fullDesc}</p>
            <p className="mt-4 rounded-xl bg-[var(--cream-2)] p-4 text-sm italic text-foreground/75">
              "{c.shortDesc}"
            </p>
          </Block>

          {/* Gallery */}
          <Block title="Photos from the field" eyebrow="See the work">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {gallery.map((src, i) => (
                <div key={i} className="overflow-hidden rounded-xl shadow-[var(--shadow-soft)]">
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="aspect-square h-full w-full object-cover transition hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </Block>

          {/* Impact tiers */}
          <Block title="What your donation does" eyebrow="Real impact">
            <div className="grid gap-3 sm:grid-cols-2">
              {tiers.map((t) => (
                <Link
                  key={t.amount}
                  to="/donate"
                  search={{ cause: c.id, amount: t.amount }}
                  className="group flex items-center justify-between gap-4 rounded-2xl border border-border/60 bg-card p-4 transition hover:border-[var(--maroon)]/40 hover:shadow-[var(--shadow-soft)]"
                >
                  <div className="min-w-0">
                    <p className="font-display text-xl font-semibold text-[var(--maroon-deep)]">
                      ₹{t.amount.toLocaleString("en-IN")}
                    </p>
                    <p className="mt-0.5 text-sm text-foreground/70">{t.impact}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-foreground/40 group-hover:text-[var(--maroon)]" />
                </Link>
              ))}
            </div>
          </Block>

          {/* Fund usage breakdown */}
          <Block title="How funds will be used" eyebrow="Full transparency">
            <div className="space-y-4">
              {breakdown.map((b) => (
                <div key={b.label}>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-foreground/85">{b.label}</span>
                    <span className="text-foreground/60">{b.pct}%</span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[var(--cream-2)]">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${b.pct}%`, background: b.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-foreground/70">
              Funds are released against milestones. Institutions must upload expense proof for
              every disbursement.
            </p>
          </Block>

          {/* Trust */}
          <Block title="Why this cause is trustworthy" eyebrow="Verified by Manavta">
            <ul className="grid gap-3 sm:grid-cols-2">
              {[
                "Institution KYC, PAN & bank verified",
                "Cause approved by platform admin",
                "Milestone-based fund disbursal",
                "Mandatory expense proof + photos",
                inst?.has80G ? "80G tax-exempt receipt" : "Regular donation receipt",
                "Public donor list (with consent)",
              ].map((x) => (
                <li
                  key={x}
                  className="flex items-start gap-2 rounded-xl bg-[var(--cream-2)] p-3 text-sm text-foreground/80"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--success)]" />
                  {x}
                </li>
              ))}
            </ul>
          </Block>

          {/* Updates timeline */}
          <Block title="Cause updates" eyebrow="What's happening">
            <ol className="relative space-y-6 border-l-2 border-[var(--cream-2)] pl-6">
              {updates.map((u, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[31px] top-1 grid h-5 w-5 place-items-center rounded-full bg-[var(--maroon)] text-white">
                    <Sparkles className="h-2.5 w-2.5" />
                  </span>
                  <p className="text-xs text-foreground/70">{u.date}</p>
                  <p className="mt-0.5 font-semibold text-foreground">{u.title}</p>
                  <p className="mt-1 text-sm text-foreground/70">{u.body}</p>
                </li>
              ))}
            </ol>
          </Block>

          {/* Top donors */}
          <Block title="Top donors" eyebrow="Thank you">
            <ul className="divide-y divide-border/60 rounded-2xl border border-border/60 bg-card">
              {topDonors.map((d, i) => (
                <li key={i} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--cream-2)] font-semibold text-[var(--maroon)]">
                      {d.name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground">{d.name}</p>
                      <p className="text-xs text-foreground/70">{d.city}</p>
                    </div>
                  </div>
                  <span className="font-display font-semibold text-[var(--maroon)]">
                    ₹{d.amount.toLocaleString("en-IN")}
                  </span>
                </li>
              ))}
            </ul>
          </Block>

          {/* FAQ */}
          <Block title="Frequently asked" eyebrow="Donor FAQs">
            <div className="divide-y divide-border/60 rounded-2xl border border-border/60 bg-card">
              {faqs.map((f) => (
                <details key={f.q} className="group p-5 [&_summary::-webkit-details-marker]:hidden">
                  <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-foreground">
                    {f.q}
                    <ChevronRight className="h-4 w-4 transition group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-sm text-foreground/70">{f.a}</p>
                </details>
              ))}
            </div>
          </Block>

          {similar.length > 0 && (
            <Block title="Similar causes" eyebrow="Keep giving">
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {similar.map((s) => (
                  <CauseCard key={s.id} cause={s} />
                ))}
              </div>
            </Block>
          )}
        </div>

        {/* Sticky donation card */}
        <aside className="lg:sticky lg:top-24 lg:self-start space-y-4">
          <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-[var(--shadow-card)]">
            <div className="flex items-baseline justify-between">
              <span className="font-display text-3xl font-semibold text-[var(--maroon)]">
                {formatINR(c.raised)}
              </span>
              <span className="text-xs text-foreground/70">of {formatINR(c.required)}</span>
            </div>
            <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[var(--cream-2)]">
              <div
                className="h-full rounded-full"
                style={{ width: `${pct}%`, background: "var(--gradient-warm)" }}
              />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-[var(--cream-2)] py-2">
                <p className="font-display text-base font-semibold text-[var(--maroon-deep)]">
                  {pct}%
                </p>
                <p className="text-[10px] uppercase tracking-wider text-foreground/70">Funded</p>
              </div>
              <div className="rounded-lg bg-[var(--cream-2)] py-2">
                <p className="font-display text-base font-semibold text-[var(--maroon-deep)]">
                  {c.donors}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-foreground/70">Donors</p>
              </div>
              <div className="rounded-lg bg-[var(--cream-2)] py-2">
                <p className="font-display text-base font-semibold text-[var(--maroon-deep)]">
                  {daysLeft}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-foreground/70">Days left</p>
              </div>
            </div>

            <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-foreground/70">
              Choose an amount
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {donationAmounts.map((a) => (
                <Link
                  key={a}
                  to="/donate"
                  search={{ cause: c.id, amount: a }}
                  className="rounded-xl border border-border bg-background px-3 py-2.5 text-center text-sm font-semibold hover:border-[var(--maroon)] hover:text-[var(--maroon)]"
                >
                  ₹{a.toLocaleString("en-IN")}
                </Link>
              ))}
            </div>
            <Link
              to="/donate"
              search={{ cause: c.id }}
              className="mt-4 block rounded-full bg-[var(--maroon)] px-5 py-3 text-center text-sm font-semibold text-[var(--cream)] hover:bg-[var(--maroon-deep)]"
            >
              Donate Now
            </Link>
            <div className="mt-3 flex gap-2">
              <button className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-white px-3 py-2 text-xs font-medium text-foreground/75 hover:bg-[var(--cream-2)]">
                <Heart className="h-3.5 w-3.5" /> Save
              </button>
              <button className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-white px-3 py-2 text-xs font-medium text-foreground/75 hover:bg-[var(--cream-2)]">
                <Share2 className="h-3.5 w-3.5" /> Share
              </button>
            </div>
            <p className="mt-3 flex items-center justify-center gap-1 text-center text-[11px] text-foreground/70">
              <ShieldCheck className="h-3 w-3" /> 100% secure · Receipt sent instantly
            </p>
          </div>

          <div className="rounded-2xl border border-border/60 bg-[var(--cream-2)] p-5 text-sm">
            <p className="flex items-center gap-2 font-semibold text-[var(--maroon-deep)]">
              <FileText className="h-4 w-4" /> Tax benefit
            </p>
            <p className="mt-2 text-foreground/70">
              {inst?.has80G
                ? "This cause is run by an 80G-certified institution. Save up to 50% tax on your donation."
                : "This institution does not currently have 80G. Donations are still 100% routed to the cause."}
            </p>
          </div>
        </aside>
      </section>

      {/* Mobile Sticky Bottom Action Bar */}
      <div className="lg:hidden fixed bottom-14 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur-md p-3 px-4 shadow-[0_-4px_16px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-between gap-3 max-w-md mx-auto">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Raised {formatINR(c.raised)}
            </p>
            <p className="text-xs font-bold text-[var(--maroon-deep)]">
              {pct}% of {formatINR(c.required)}
            </p>
          </div>
          <Link
            to="/donate"
            search={{ cause: c.id }}
            className="flex items-center gap-1.5 rounded-full bg-[var(--maroon)] px-5 py-2.5 text-xs font-bold text-white shadow-md active:scale-95 transition"
          >
            <Heart className="h-4 w-4 fill-white" /> Donate Now
          </Link>
        </div>
      </div>
    </SiteShell>
  );
}

function Block({
  title,
  eyebrow,
  children,
}: {
  title: string;
  eyebrow?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      {eyebrow && (
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
          {eyebrow}
        </p>
      )}
      <h2 className="mt-1 font-display text-2xl font-semibold text-[var(--maroon-deep)]">
        {title}
      </h2>
      <div className="mt-5">{children}</div>
    </div>
  );
}
function Mini({
  label,
  value,
  accent,
  icon: Icon,
}: {
  label: string;
  value: string;
  accent?: boolean;
  icon?: any;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4">
      <div className="flex items-center gap-1.5 text-foreground/70">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        <p className="text-xs">{label}</p>
      </div>
      <p
        className={`mt-1.5 font-display text-lg font-semibold ${accent ? "text-[var(--maroon)]" : "text-foreground"}`}
      >
        {value}
      </p>
    </div>
  );
}
